import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import {
  Typography,
  Button,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { Header, Page, Content } from '@backstage/core-components';
import { CookieAuthRefreshProvider } from '@backstage/plugin-auth-react';

// Gary's own queue UI, linked from the page header. The public hostname, not
// the box's LAN address: the same page is opened from outside the office.
const GARY_URL = 'https://gary.devops.salestrak.io';

// The AI QA dashboard: run GROUPS, one per feature / body of work. A group
// collects the AI runner's outputs over that feature's life — handoff
// snapshots of the designer's sandbox (versioned), the PR-time diff runs
// (vs the sandbox and vs dev), and free runs — each linking to its report.

interface RunMeta {
  runId: string;
  kind: 'handoff' | 'diff-sandbox' | 'diff-dev' | 'free';
  createdAt: string;
  base?: string;
  document?: string;
  label?: string;
  summary?: {
    surfaces?: number;
    goals?: number;
    findings?: number;
    dataFindings?: number;
  };
}

interface PrRef {
  number: number;
  title: string;
  url: string;
}

interface Group {
  id: string;
  name: string;
  branch?: string;
  sandboxBase?: string;
  /** Linked PRs — a feature can ship across several. */
  prs: PrRef[];
  createdAt: string;
  updatedAt: string;
  runs: RunMeta[];
}

interface Pull {
  number: number;
  title: string;
  url: string;
  branch?: string;
  author?: string;
}

interface Sandbox {
  slug: string;
  branch?: string;
}

const KIND_LABELS: Record<RunMeta['kind'], string> = {
  handoff: 'Handoff',
  'diff-sandbox': 'Diff vs sandbox',
  'diff-dev': 'Diff vs dev',
  free: 'Free',
};

const KIND_ORDER: RunMeta['kind'][] = ['handoff', 'diff-sandbox', 'diff-dev', 'free'];

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  search: { minWidth: 320 },
  grow: { flexGrow: 1 },
  expandCell: { width: 40, padding: theme.spacing(0, 1) },
  runsBlock: { padding: theme.spacing(2, 2, 2, 7) },
  kindTitle: {
    fontWeight: 600,
    textTransform: 'uppercase',
    fontSize: 12,
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    margin: theme.spacing(1.5, 0, 0.5),
  },
  runRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.5, 0),
  },
  muted: { color: theme.palette.text.secondary },
  linkCell: { minWidth: 240 },
  linkCellInner: { display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) },
  rowPicker: { width: 220 },
  prPicker: {
    minWidth: 260,
    maxWidth: 420,
    // Chips sit centred in the field rather than riding its top edge.
    '& .MuiAutocomplete-inputRoot': { alignItems: 'center' },
  },
  prChip: { maxWidth: 180 },
  // Only the label navigates (see renderTags), so it carries the chip's colour.
  prChipLink: { color: 'inherit', textDecoration: 'none' },
}));

function versionLabel(run: RunMeta, handoffs: RunMeta[]): string {
  const i = handoffs.findIndex(h => h.runId === run.runId);
  return i >= 0 ? `v${i + 1}` : '';
}

function summaryText(run: RunMeta): string {
  const s = run.summary;
  if (!s) return '';
  const bits: string[] = [];
  if (s.surfaces != null) bits.push(`${s.surfaces} surfaces`);
  if (s.goals) bits.push(`${s.goals} goals`);
  if (s.findings != null) bits.push(`${s.findings} findings`);
  if (s.dataFindings) bits.push(`${s.dataFindings} data`);
  return bits.join(' · ');
}

// The provider mints and refreshes the plugin's session cookie, which is what
// lets report tabs and their images load as plain browser navigations. It
// also completes the ?open= bounce: a pasted artifact URL with no cookie
// redirects here, and once the cookie exists the page forwards to it.
export const AiQaPage = () => (
  <CookieAuthRefreshProvider pluginId="ai-qa">
    <AiQaPageContent />
  </CookieAuthRefreshProvider>
);

const AiQaPageContent = () => {
  const classes = useStyles();
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [artifactBase, setArtifactBase] = useState('');

  // New-group dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newBranch, setNewBranch] = useState('');
  const [creating, setCreating] = useState(false);

  // PR + sandbox linking
  const [pulls, setPulls] = useState<Pull[]>([]);
  const [sandboxes, setSandboxes] = useState<Sandbox[]>([]);
  const [linking, setLinking] = useState<Record<string, boolean>>({});
  // Unlinking a PR asks first — the chip's ✕ sits a pixel from the link.
  const [prToRemove, setPrToRemove] = useState<{ group: Group; pr: PrRef } | null>(null);

  const fetchGroups = useCallback(async () => {
    try {
      const base = await discovery.getBaseUrl('ai-qa');
      setArtifactBase(base);
      const res = await fetchApi.fetch(`${base}/groups`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = (await res.json()) as { groups: Group[] };
      setGroups(data.groups);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [discovery, fetchApi]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // A pasted artifact URL with no session lands here as /ai-qa?open=<path>.
  // The CookieAuthRefreshProvider above has minted the cookie by the time
  // this renders, so forward straight to the artifact.
  useEffect(() => {
    if (!artifactBase) return;
    const open = new URLSearchParams(window.location.search).get('open');
    if (open && /^[a-z0-9-]+\/[a-z0-9-]+\//.test(open) && !open.includes('..')) {
      window.location.replace(`${artifactBase}/artifact/${open}`);
    }
  }, [artifactBase]);

  useEffect(() => {
    (async () => {
      try {
        const base = await discovery.getBaseUrl('ai-qa');
        const res = await fetchApi.fetch(`${base}/pulls`);
        if (res.ok) setPulls(((await res.json()) as { pulls: Pull[] }).pulls);
      } catch {
        // PR linking degrades to nothing; the table still works.
      }
    })();
    (async () => {
      try {
        // The preview-envs plugin already knows every sandbox — its list IS
        // the "Linked sandbox" dropdown for handoff-stage groups.
        const base = await discovery.getBaseUrl('preview-envs');
        const res = await fetchApi.fetch(`${base}/preview-envs`);
        if (res.ok) {
          const data = (await res.json()) as { envs: { slug: string; branch?: string }[] };
          setSandboxes(data.envs.map(e => ({ slug: e.slug, branch: e.branch })));
        }
      } catch {
        // Sandbox linking degrades to nothing; the table still works.
      }
    })();
  }, [discovery, fetchApi]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(g =>
      [
        g.name,
        g.id,
        g.branch,
        g.sandboxBase,
        ...g.prs.flatMap(p => [p.title, String(p.number)]),
      ]
        .filter(Boolean)
        .some(v => v!.toLowerCase().includes(q)),
    );
  }, [groups, search]);

  const createGroup = async () => {
    setCreating(true);
    try {
      const base = await discovery.getBaseUrl('ai-qa');
      const res = await fetchApi.fetch(`${base}/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName, branch: newBranch || undefined }),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? res.statusText);
      setCreateOpen(false);
      setNewName('');
      setNewBranch('');
      await fetchGroups();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // Replaces the group's whole PR list — the multi-select hands back the
  // full selection on every add or remove.
  const linkPrs = async (group: Group, selected: PrRef[]) => {
    setLinking(prev => ({ ...prev, [group.id]: true }));
    try {
      const base = await discovery.getBaseUrl('ai-qa');
      const res = await fetchApi.fetch(`${base}/groups/${group.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prs: selected.map(p => ({ number: p.number, title: p.title, url: p.url })),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? res.statusText);
      await fetchGroups();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLinking(prev => ({ ...prev, [group.id]: false }));
    }
  };

  const linkSandbox = async (group: Group, sandbox: Sandbox | null) => {
    setLinking(prev => ({ ...prev, [group.id]: true }));
    try {
      const base = await discovery.getBaseUrl('ai-qa');
      const res = await fetchApi.fetch(`${base}/groups/${group.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sandboxBase: sandbox ? `${sandbox.slug}.sandbox.salestrak.io` : '',
          branch: sandbox?.branch ?? '',
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? res.statusText);
      await fetchGroups();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLinking(prev => ({ ...prev, [group.id]: false }));
    }
  };

  const reportUrl = (group: Group, run: RunMeta) =>
    `${artifactBase}/artifact/${group.id}/${run.runId}/${run.document ?? 'report.html'}`;

  const runCounts = (g: Group) =>
    KIND_ORDER.filter(k => g.runs.some(r => r.kind === k))
      .map(k => `${g.runs.filter(r => r.kind === k).length} ${KIND_LABELS[k].toLowerCase()}`)
      .join(' · ') || 'no runs yet';

  return (
    <Page themeId="tool">
      <Header
        title="Gary"
        subtitle="AI QA — run groups per feature: handoff snapshots, diff runs, free runs"
      >
        <Button
          variant="contained"
          color="primary"
          href={GARY_URL}
          target="_blank"
          rel="noopener"
          endIcon={<OpenInNewIcon />}
        >
          Go to Gary
        </Button>
      </Header>
      <Content>
        <div className={classes.toolbar}>
          <TextField
            className={classes.search}
            variant="outlined"
            size="small"
            placeholder="Search by name, branch, or PR…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className={classes.grow} />
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchGroups()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" color="primary" onClick={() => setCreateOpen(true)}>
            New group
          </Button>
        </div>

        {error && (
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.expandCell} />
                  <TableCell>Name</TableCell>
                  <TableCell>Sandbox</TableCell>
                  <TableCell>PR</TableCell>
                  <TableCell>Runs</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography className={classes.muted}>
                        {search
                          ? 'No groups match the search.'
                          : 'No groups yet — create one, or run Gary against a sandbox or PR.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {visible.map(group => (
                  <Fragment key={group.id}>
                    <TableRow hover onClick={() => setExpanded(prev => ({ ...prev, [group.id]: !prev[group.id] }))}>
                      <TableCell className={classes.expandCell}>
                        <IconButton size="small">
                          {expanded[group.id] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {group.name}
                        </Typography>
                      </TableCell>
                      {/* The group-level links live ON the row — one sandbox
                          and any number of PRs per group. stopPropagation
                          keeps picker clicks from toggling the row. */}
                      <TableCell className={classes.linkCell} onClick={e => e.stopPropagation()}>
                        <div className={classes.linkCellInner}>
                          <Autocomplete
                            className={classes.rowPicker}
                            size="small"
                            options={sandboxes}
                            value={sandboxes.find(s => group.sandboxBase?.startsWith(`${s.slug}.`)) ?? null}
                            getOptionLabel={s => s.slug}
                            renderInput={params => (
                              <TextField {...params} variant="outlined" placeholder="Link sandbox…" />
                            )}
                            onChange={(_e, value) => linkSandbox(group, value)}
                            disabled={!!linking[group.id]}
                          />
                          {group.sandboxBase && (
                            <Tooltip title={`app.${group.sandboxBase}`}>
                              <IconButton
                                size="small"
                                component="a"
                                href={`https://app.${group.sandboxBase}`}
                                target="_blank"
                              >
                                <OpenInNewIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={classes.linkCell} onClick={e => e.stopPropagation()}>
                        <Autocomplete
                          multiple
                          className={classes.prPicker}
                          size="small"
                          // Already-linked PRs stay selectable even once they
                          // close and drop out of the open-PR list.
                          options={[
                            ...group.prs.filter(p => !pulls.some(o => o.number === p.number)),
                            ...pulls,
                          ]}
                          value={group.prs}
                          getOptionSelected={(o, v) => o.number === v.number}
                          getOptionLabel={p => `#${p.number} ${p.title}`}
                          renderTags={(selected, getTagProps) =>
                            selected.map((p, i) => (
                              <Tooltip key={p.number} title={p.title}>
                                <Chip
                                  {...getTagProps({ index: i })}
                                  className={classes.prChip}
                                  size="small"
                                  // NOT an anchor: the delete icon sits inside
                                  // the chip, and a click on it inside a link
                                  // still follows the href — which opened the
                                  // PR instead of unlinking it. Only the label
                                  // navigates.
                                  label={
                                    <MuiLink
                                      href={p.url}
                                      target="_blank"
                                      rel="noopener"
                                      className={classes.prChipLink}
                                    >
                                      #{p.number}
                                    </MuiLink>
                                  }
                                  icon={<OpenInNewIcon fontSize="inherit" />}
                                />
                              </Tooltip>
                            ))
                          }
                          renderInput={params => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={group.prs.length ? 'Add PR…' : 'Link PR…'}
                            />
                          )}
                          onChange={(_e, value) => {
                            // Covers both the chip's ✕ and a backspace in the
                            // field; only additions apply without asking.
                            const removed = group.prs.find(
                              p => !value.some(v => v.number === p.number),
                            );
                            if (removed) {
                              setPrToRemove({ group, pr: removed });
                              return;
                            }
                            linkPrs(group, value);
                          }}
                          disabled={!!linking[group.id]}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className={classes.muted}>
                          {runCounts(group)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className={classes.muted}>
                          {new Date(group.updatedAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} style={{ padding: 0, borderBottom: expanded[group.id] ? undefined : 'none' }}>
                        <Collapse in={!!expanded[group.id]} timeout="auto" unmountOnExit>
                          <GroupDetail group={group} classes={classes} reportUrl={reportUrl} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        <Dialog open={!!prToRemove} onClose={() => setPrToRemove(null)} maxWidth="xs">
          <DialogTitle>Unlink PR #{prToRemove?.pr.number}?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              <strong>
                #{prToRemove?.pr.number} {prToRemove?.pr.title}
              </strong>{' '}
              stops being linked to {prToRemove?.group.name}. The pull request
              itself is untouched, and the group keeps every run it has.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPrToRemove(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                if (prToRemove) {
                  linkPrs(
                    prToRemove.group,
                    prToRemove.group.prs.filter(
                      p => p.number !== prToRemove.pr.number,
                    ),
                  );
                }
                setPrToRemove(null);
              }}
            >
              Unlink
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>New run group</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Feature name"
              helperText="Names the feature or body of work — becomes the group key."
              value={newName}
              onChange={e => setNewName(e.target.value)}
            />
            <TextField
              fullWidth
              margin="dense"
              label="Branch (optional)"
              helperText="e.g. team-design/my-feature — the sandbox branch."
              value={newBranch}
              onChange={e => setNewBranch(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              disabled={creating || !newName.trim()}
              onClick={createGroup}
            >
              {creating ? 'Creating…' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Content>
    </Page>
  );
};

const GroupDetail = ({
  group,
  classes,
  reportUrl,
}: {
  group: Group;
  classes: ReturnType<typeof useStyles>;
  reportUrl: (group: Group, run: RunMeta) => string;
}) => {
  const handoffs = group.runs.filter(r => r.kind === 'handoff');
  return (
    <div className={classes.runsBlock}>
      {group.runs.length === 0 && (
        <Typography variant="body2" className={classes.muted}>
          No runs yet. Runs appear here on their own when they finish on the
          box — a run against{' '}
          <code>{group.sandboxBase ?? group.id}</code> lands in this group.
        </Typography>
      )}
      {KIND_ORDER.map(kind => {
        const runs = group.runs.filter(r => r.kind === kind);
        if (runs.length === 0) return null;
        // Newest first for reading; handoff version numbers count from oldest.
        const ordered = [...runs].reverse();
        return (
          <div key={kind}>
            <Typography className={classes.kindTitle}>{KIND_LABELS[kind]}</Typography>
            {ordered.map(run => (
              <div key={run.runId} className={classes.runRow}>
                {kind === 'handoff' && (
                  <Chip size="small" variant="outlined" label={versionLabel(run, handoffs)} />
                )}
                <Typography variant="body2">
                  {new Date(run.createdAt).toLocaleString()}
                </Typography>
                {run.base && (
                  <Typography variant="body2" className={classes.muted}>
                    {run.base}
                  </Typography>
                )}
                <Typography variant="body2" className={classes.muted}>
                  {run.label ?? summaryText(run)}
                </Typography>
                <MuiLink href={reportUrl(group, run)} target="_blank" rel="noopener">
                  Report <OpenInNewIcon style={{ fontSize: 13, verticalAlign: 'middle' }} />
                </MuiLink>
              </div>
            ))}
          </div>
        );
      })}

    </div>
  );
};
